import { css } from "@emotion/css";
import { LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableRow, LegacyDataTableRowHeaderColumn } from "@octopusdeploy/design-system-components";
import { borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import type { DeploymentFreezeDetailEnvironment, DeploymentFreezeScopeDetail, DeploymentFreezeScopeType, GetDeploymentFreezeByIdResponse } from "@octopusdeploy/octopus-server-client";
import { useEffect, useState } from "react";
import { repository } from "~/clientInstance";
import type { DoBusyTask } from "~/components/DataBaseComponent/index";
import { DeploymentFreezeScopeTableRow } from "~/components/DeploymentFreezes/EditScopeTable/DeploymentFreezeScopeTableRow";
import NumberedPagingBar from "~/components/PagingBaseComponent/NumberedPagingBar";
interface DeploymentFreezeScopeTableProps {
    scopeDetails: DeploymentFreezeScopeDetail[];
    onRemove: (item: DeploymentFreezeScopeDetail) => void;
    onEnvironmentUpdate: (item: DeploymentFreezeScopeDetail, selectedEnvironments: DeploymentFreezeDetailEnvironment[]) => void;
    scopeType: DeploymentFreezeScopeType;
    doBusyTask: DoBusyTask;
    focussedScopeCellId: string;
    onFocusedScopeCell: (id: string) => void;
    onScopeCellLostFocus: () => void;
    deploymentFreezeId?: string;
}
export const DeploymentFreezeScopeTable = ({ scopeDetails, scopeType, onRemove, onEnvironmentUpdate, doBusyTask, focussedScopeCellId, onScopeCellLostFocus, onFocusedScopeCell, deploymentFreezeId }: DeploymentFreezeScopeTableProps) => {
    const pageSize = 30;
    const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
    const [paginatedScopeItems, setPaginatedItems] = useState<DeploymentFreezeScopeDetail[]>(scopeDetails);
    const [freezeDetail, setFreezeDetail] = useState<GetDeploymentFreezeByIdResponse>();
    useEffect(() => {
        const pagedItems = scopeDetails.slice(pageSize * currentPageIndex, pageSize * (currentPageIndex + 1));
        setPaginatedItems(pagedItems);
    }, [scopeDetails, currentPageIndex]);
    useEffect(() => {
        if (deploymentFreezeId) {
            const fetchFreezeDetail = async () => {
                try {
                    const detail = await repository.DeploymentFreezes.get(deploymentFreezeId);
                    setFreezeDetail(detail);
                }
                catch (error) {
                    logger.error("Failed to fetch deployment freeze details");
                }
            };
            fetchFreezeDetail();
        }
    }, [deploymentFreezeId]);
    return (<div>
            <LegacyDataTable className={tableStyles} title="Deployment Freeze Scope Table">
                <LegacyDataTableHeader>
                    <LegacyDataTableRow>
                        <LegacyDataTableRowHeaderColumn>{scopeType}</LegacyDataTableRowHeaderColumn>
                        <LegacyDataTableRowHeaderColumn>Environments</LegacyDataTableRowHeaderColumn>
                        <LegacyDataTableRowHeaderColumn />
                    </LegacyDataTableRow>
                </LegacyDataTableHeader>
                <LegacyDataTableBody>
                    {paginatedScopeItems.map((item) => (<DeploymentFreezeScopeTableRow freezeDetail={freezeDetail} scopeType={scopeType} key={item.Id} scopeItem={item} onRemove={onRemove} onEnvironmentUpdate={onEnvironmentUpdate} doBusyTask={doBusyTask} focussedScopeCellId={focussedScopeCellId} onFocusedScopeCell={onFocusedScopeCell} onScopeCellLostFocus={onScopeCellLostFocus}/>))}
                </LegacyDataTableBody>
            </LegacyDataTable>
            <NumberedPagingBar totalItems={scopeDetails.length} currentPageIndex={currentPageIndex} pageSize={pageSize} onPageSelected={(_, index) => setCurrentPageIndex(index)}/>
        </div>);
};
const tableBorder = `${borderWidth[1]} solid ${themeTokens.color.border.primary}`;
const tableStyles = css({
    width: "100%",
    th: {
        padding: `${space[12]} ${space[16]}`,
        paddingRight: 0,
        textAlign: "left",
        font: text.body.regular.medium,
        position: "relative",
        width: "auto",
        ":not(:last-child)": {
            ":after": {
                content: "''",
                position: "absolute",
                height: "50%",
                right: 0,
                top: "25%",
                borderRight: tableBorder,
            },
        },
    },
    td: {
        padding: `${space[8]} ${space[12]}`,
        borderTop: tableBorder,
        verticalAlign: "top",
    },
});
